import React from 'react'

import * as c from '../../../common'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Modal from '@mui/material/Modal'
import Paper from '@mui/material/Paper'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import InputBase from '@mui/material/InputBase'
import styled from '@mui/material/styles/styled'
import { SxProps, Theme, Typography } from '@mui/material'

import Pad from '../Pad/Pad'

//@ts-ignore
import styles from './FinancingTable.module.css'

export interface FinancingTableProps {
  alarmElectricity: number
  alarmSolar: number
  company: string
  contentUser: 'SOLAR' | 'NORMAL' | 'ALARM'
  financingInterest: number
  financingYears: number
  payPerMonth: number
  percentIncrease: number
  powerBill: number
  ppaTwoInput: number
  ppaTwoPayments: number[]
  pto: number
  solarBill: number
  totalEfficiencyAmount: number
  hideComparisonBtn?: boolean
}

const formatMoney = (val?: number | null): string => {
  if (val === undefined || val === null || isNaN(val)) return '0.00'
  return val.toLocaleString('en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export default React.memo<FinancingTableProps>(function FinancingTable({
  alarmElectricity,
  alarmSolar,
  company,
  contentUser,
  financingInterest,
  financingYears,
  payPerMonth,
  percentIncrease,
  powerBill,
  ppaTwoInput,
  ppaTwoPayments,
  solarBill,
  totalEfficiencyAmount,
  hideComparisonBtn,
}) {
  const isPPA = contentUser === 'SOLAR'
  const [years, setYears] = React.useState(2)
  const [open, setOpen] = React.useState(false)

  const handleClose = React.useCallback(() => {
    setOpen((current) => !current)
  }, [setOpen])

  const handleYears = React.useCallback(
    (event: SelectChangeEvent) => {
      setYears(parseInt(event.target.value))
    },
    [setYears],
  )

  const monthlyElectricPayment = React.useMemo(() => {
    if (!powerBill) return []
    let _monthlyElectricPayment = new Array<number>(30).fill(0)

    _monthlyElectricPayment[0] = powerBill

    for (let i = 1; i <= 30; i++) {
      const prevMonth = _monthlyElectricPayment[i - 1] as number
      _monthlyElectricPayment[i] = Math.round(
        prevMonth + prevMonth * (percentIncrease / 100),
      )
    }

    return _monthlyElectricPayment
  }, [powerBill, percentIncrease])

  const yearlyElectricPayments = React.useMemo(() => {
    const firstYear = powerBill * 12
    const _yearly = [firstYear]
    for (let i = 1; i < ppaTwoPayments.length; i++) {
      _yearly[i] = _yearly[i - 1]! * (1 + percentIncrease / 100)
    }
    return _yearly
  }, [percentIncrease, powerBill, ppaTwoPayments.length])

  const yearlyPowerMinusPPA = React.useMemo(
    () =>
      c.zipWith(yearlyElectricPayments, ppaTwoPayments, (a, b) =>
        Math.round(a - b),
      ),
    [ppaTwoPayments, yearlyElectricPayments],
  )

  const totalSolarAmt = React.useMemo(() => {
    if (!monthlyElectricPayment.length) return 0
    const _savings = new Array(years)
      .fill(0)
      .map((_, i) => (monthlyElectricPayment[i]! - solarBill) * 12)
    console.log('Savings TABLE: ', _savings)
    let _totalSolarAmt = 0
    _savings.forEach((value) => {
      if (value < 0) {
        _totalSolarAmt += value
      } else {
        _totalSolarAmt += value
      }
    })

    return _totalSolarAmt
  }, [monthlyElectricPayment, solarBill, years])

  React.useEffect(() => {
    // No cleanup here left <body class="no-scroll"> stuck for the whole report
    // if this unmounted while `open` (same leak as B150 Modal). The old
    // addEventListener/removeEventListener pair ran on the same tick - dead
    // code, never an active listener - so it is dropped, not switched on.
    if (!open) {
      return undefined
    }

    document.body.classList.add('no-scroll')

    return (): void => {
      document.body.classList.remove('no-scroll')
    }
  }, [open])

  const StyledSelect = React.useMemo(() => {
    return styled(InputBase)(({ theme }) => ({
      'label + &': {
        marginTop: theme.spacing(3),
      },
      '& .MuiInputBase-input': {
        backgroundColor: theme.palette.background.paper,
        border: '1px solid #ced4da',
        borderRadius: 4,
        fontFamily: [
          '-apple-system',
          '"Apple Color Emoji"',
          '"Helvetica Neue"',
          '"Segoe UI Emoji"',
          '"Segoe UI Symbol"',
          '"Segoe UI"',
          'Arial',
          'BlinkMacSystemFont',
          'Roboto',
          'sans-serif',
        ].join(','),
        fontSize: 16,
        padding: '10px 26px 10px 12px',
        position: 'relative',
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        // Use the system font instead of the default Roboto font.

        '&:focus': {
          borderColor: c.companyColors[company],
          borderRadius: 1,
          boxShadow: `0 0 0 1px ${c.companyColors[company]}`,
        },
      },
    }))
  }, [company])

  const buttonModalStyle = React.useMemo((): SxProps<Theme> => {
    return {
      alignSelf: 'center',
      backgroundColor: c.companyColors[company],
    }
  }, [company])

  const ppa2MinusPowerBill = isPPA
    ? Math.round(Math.abs(powerBill - ppaTwoInput))
    : -1
  const ppa2LessThanPower = ppaTwoInput < powerBill

  const totalPowerComparison = yearlyElectricPayments
    .slice(0, years)
    .reduce((a, b) => a + b, 0)
  const totalPPA2Comparison = ppaTwoPayments
    .slice(0, years)
    .reduce((a, b) => a + b, 0)

  return (
    <React.Fragment>
      <div className={styles['container']}>
        {contentUser !== 'ALARM' && (
          <div className={styles['row']}>
            <div className={styles['title-row']}>Cash</div>
            {/* `toFixed(2)` with a bare `$` printed "$23400.00" on Diane
                Pacheco's report and "$14950.00" on Anna Chiang's, both a few
                lines under "$322.34" and "$15,560" which do carry separators.
                Same defect the loan amount had in 486adf20; these two were
                missed because they format the number themselves instead of
                going through `money`. `money` is the whole fix - it adds the
                separators AND the two decimals.

                Guarded because `money` prints whatever it is given and
                `payPerMonth` is `ipv / div`: at 0% interest `div` is
                `1 - Math.pow(1, -n)` = 0 and `ipv` is 0, so it is 0/0 = NaN. */}
            <div className={styles['input-dark']}>
              ${Math.round(totalEfficiencyAmount).toLocaleString('en')}
            </div>
          </div>
        )}

        <div className={styles['row']}>
          <div className={styles['title-row']}>
            Financing
            <span style={{ fontSize: 8, margin: 0, lineHeight: 0 }}>
              ({((financingInterest || 0) * 100).toFixed(1)}%)
            </span>
          </div>
          <div className={styles['input-dark']}>
            ${formatMoney(payPerMonth)}
          </div>
        </div>
        {/* 
        {isPPA && (
          <div className={styles['row']}>
            <div className={styles['title-row']}>PPA1</div>
            <div className={styles['input']}>
              $ {Math.round(ppa).toLocaleString('en')}
            </div>
          </div>
        )} */}

        {isPPA && (
          <div className={styles['row']}>
            <div className={styles['title-row']}>PPA</div>
            <div
              className={styles['input']}
              style={{
                color: ppa2LessThanPower ? 'green' : undefined,
              }}
            >
              ${ppa2MinusPowerBill}
            </div>
          </div>
        )}

        {contentUser === 'ALARM' && (
          <div className={styles['row']}>
            <div className={styles['title-row']}>Alarm + Electricity</div>
            <div className={styles['input']}>
              ${formatMoney(alarmElectricity)}
            </div>
          </div>
        )}

        {contentUser === 'ALARM' && (
          <div
            className={contentUser === 'ALARM' ? styles['row'] : styles['hide']}
          >
            <div className={styles['title-row']}>Alarm + Solar</div>
            <div className={styles['input']}>${formatMoney(alarmSolar)}</div>
          </div>
        )}

        {contentUser === 'NORMAL' && c.CODENAME !== 'nirvana' && (
          <div className={styles['row']}>
            <div className={styles['title-row']}>Solar</div>
            <div className={styles['input-dark']}>
              {isNaN(
                Math.abs((monthlyElectricPayment[0] || 0) - (solarBill || 0)),
              )
                ? ''
                : `$${Math.abs(
                    (monthlyElectricPayment[0] || 0) - (solarBill || 0),
                  ).toLocaleString('en')}`}
            </div>
          </div>
        )}
      </div>
      {!hideComparisonBtn && (
        <Button onClick={handleClose} sx={buttonModalStyle} variant="contained">
          SHOW COMPARISON
        </Button>
      )}
      <Modal open={open} onClose={handleClose}>
        <Box component={Paper} sx={sx['modalBox']}>
          <HighlightOffIcon sx={sx['closeModalIcon']} onClick={handleClose} />

          <InputLabel id="demo-simple-select-label" sx={sx['labelYears']}>
            Years
          </InputLabel>
          <Select
            id="demo-simple-select"
            input={<StyledSelect />}
            labelId="demo-simple-select-label"
            onChange={handleYears}
            sx={sx['formControl']}
            value={years.toString()}
          >
            <MenuItem value={2}>2</MenuItem>
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
          </Select>

          <Pad amt={20} />

          <div className={styles['container-table-modal']}>
            {contentUser !== 'ALARM' && (
              <div className={styles['row-modal']}>
                <div className={styles['title-row-modal']}>Cash</div>
                <div className={styles['input-dark']}>
                  {/* Same value either way - isPPA used to skip the
                      toLocaleString formatting its sibling branch has,
                      printing "$23400" instead of "$23,400" for PPA
                      customers only. Same defect family as the SHOW
                      COMPARISON fix (13020768561). */}
                  ${''}
                  {Math.round(totalEfficiencyAmount).toLocaleString('en')}
                </div>
              </div>
            )}

            <div className={styles['row-modal']}>
              <div className={styles['title-row-modal']}>
                Financing
                <span style={{ fontSize: 8, margin: 0, lineHeight: 0 }}>
                  ({((financingInterest || 0) * 100).toFixed(1)}%)
                </span>
              </div>
              <div className={styles['input-dark']}>
                ${formatMoney(payPerMonth)}
              </div>
            </div>

            {isPPA && (
              <div className={styles['row-modal']}>
                <div className={styles['title-row-modal']}>PPA</div>
                <div
                  className={styles['input']}
                  style={{
                    color: ppa2LessThanPower ? 'green' : undefined,
                  }}
                >
                  ${ppa2MinusPowerBill}
                </div>
              </div>
            )}

            {contentUser === 'ALARM' && (
              <div className={styles['row-modal']}>
                <div className={styles['title-row-modal']}>
                  Alarm + Electricity
                </div>
                <div className={styles['input']}>
                  ${formatMoney(alarmElectricity)}
                </div>
              </div>
            )}

            {contentUser === 'ALARM' && (
              <div
                className={
                  contentUser === 'ALARM' ? styles['row-modal'] : styles['hide']
                }
              >
                <div className={styles['title-row-modal']}>Alarm + Solar</div>
                <div className={styles['input']}>
                  ${formatMoney(alarmSolar)}
                </div>
              </div>
            )}

            {(isPPA || contentUser === 'NORMAL') &&
              c.CODENAME !== 'nirvana' && (
                <div className={styles['row-modal']}>
                  <div className={styles['title-row-modal']}>Solar</div>
                  <div className={styles['input-dark']}>
                    {isNaN(
                      Math.abs(
                        (monthlyElectricPayment[0] || 0) - (solarBill || 0),
                      ),
                    )
                      ? ''
                      : `$${Math.abs(
                          (monthlyElectricPayment[0] || 0) - (solarBill || 0),
                        ).toLocaleString('en')}`}
                  </div>
                </div>
              )}
          </div>

          <Pad amt={20} />

          <TableContainer component={Paper} sx={sx['table']}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={sx['tableCell']}>
                    Year
                  </TableCell>

                  {contentUser !== 'ALARM' && (
                    <TableCell align="center" sx={sx['tableCell']}>
                      Cash
                    </TableCell>
                  )}

                  <TableCell align="center" sx={sx['tableCell']}>
                    Financing
                  </TableCell>
                  {c.CODENAME !== 'nirvana' && (
                    <TableCell align="center" sx={sx['tableCell']}>
                      {isPPA ? 'PPA Savings' : 'Solar Savings'}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {new Array(years).fill(0).map((_, i) => (
                  <TableRow key={years + '>' + i}>
                    <TableCell align="center" sx={sx['tableCell']}>
                      {i + 1}
                    </TableCell>

                    {contentUser !== 'ALARM' && (
                      <TableCell align="center" sx={sx['tableCell']}>
                        $
                        {i === 0
                          ? Math.round(totalEfficiencyAmount).toLocaleString(
                              'en',
                            )
                          : 0}
                      </TableCell>
                    )}

                    <TableCell align="center" sx={sx['tableCell']}>
                      {i + 1 > financingYears
                        ? '$0'
                        : `$${formatMoney((payPerMonth || 0) * 12)}`}
                    </TableCell>
                    {c.CODENAME !== 'nirvana' && (
                      <TableCell
                        align="center"
                        sx={
                          isPPA
                            ? yearlyPowerMinusPPA[i]! > 0
                              ? sx['tableCellGreen']
                              : sx['tableCell']
                            : Math.round(
                                ((monthlyElectricPayment[i] || 0) -
                                  (solarBill || 0)) *
                                  12,
                              ) <= 0
                            ? sx['tableCell']
                            : sx['tableCellGreen']
                        }
                      >
                        {/* Yearly totals (monthly diff * 12) with no
                            toLocaleString - same defect family as the Cash
                            row above and the SHOW COMPARISON fix
                            (13020768561), just one column over. */}
                        $
                        {isPPA
                          ? Math.abs(yearlyPowerMinusPPA[i]!).toLocaleString(
                              'en',
                            )
                          : solarBill && monthlyElectricPayment[i]
                          ? Math.abs(
                              ((monthlyElectricPayment[i] || 0) -
                                (solarBill || 0)) *
                                12,
                            ).toLocaleString('en')
                          : ''}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer>
            <Table sx={sx['tableTotal']}>
              <TableHead>
                <TableRow sx={sx['tableRowTotal']}>
                  <TableCell
                    align="center"
                    sx={sx['tableCellHiddenTotal']}
                  ></TableCell>

                  {contentUser !== 'ALARM' && (
                    <TableCell align="center" sx={sx['tableCellTotal']}>
                      <Typography sx={sx['totalText']}>Total:</Typography>
                      <span>
                        {`$${
                          totalEfficiencyAmount
                            ? Math.round(totalEfficiencyAmount).toLocaleString(
                                'en',
                              )
                            : 0
                        }`}
                      </span>
                    </TableCell>
                  )}

                  <TableCell align="center" sx={sx['tableCellTotal']}>
                    <Typography sx={sx['totalText']}>Total:</Typography>
                    <span>
                      {`$${formatMoney((payPerMonth || 0) * 12 * years)}`}
                    </span>
                  </TableCell>
                  {c.CODENAME !== 'nirvana' && (
                    <TableCell align="center" sx={sx['tableCellTotal']}>
                      <Typography sx={sx['totalText']}>Total:</Typography>
                      {isPPA && (
                        <span
                          style={
                            totalPowerComparison > totalPPA2Comparison
                              ? { color: 'green' }
                              : undefined
                          }
                        >
                          $
                          {Math.abs(
                            Math.round(
                              totalPowerComparison - totalPPA2Comparison,
                            ),
                          )}
                        </span>
                      )}

                      {!isPPA && solarBill && (
                        <span
                          style={
                            totalSolarAmt < 0 ? undefined : { color: 'green' }
                          }
                        >
                          ${Math.abs(Math.round(totalSolarAmt))}
                        </span>
                      )}

                      {!isPPA && !solarBill && <span>0</span>}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
        </Box>
      </Modal>
    </React.Fragment>
  )
})

const sx = {
  closeModalIcon: {
    cursor: 'pointer',
    position: 'absolute',
    right: 10,
    top: 10,
  },
  formControl: {
    width: '100px',
  },
  labelYears: {
    fontSize: '18px',
    textAlign: 'center',
  },
  modalBox: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    left: '50%',
    padding: '20px',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
  table: {
    boxSizing: 'border-box',
    margin: 0,
    minWidth: '580px',
  },
  tableCell: {
    fontSize: '22px',
  },
  tableCellGreen: {
    color: 'green',
    fontSize: '22px',
  },
  tableTotal: {
    width: '580px',
  },
  tableRowTotal: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  tableCellHiddenTotal: {
    fontSize: '22px',
    visibility: 'hidden',
    width: '70px',
  },
  tableCellTotal: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '22px',
  },
  totalText: {
    fontSize: '22px',
    fontWeight: '700',
  },
}
